import { cn } from "@/lib/utils";

export interface AgrupadorFechaProps {
  /**
   * "Hoy", "Ayer" o la fecha larga ("14 JUN 2026"). NUNCA un timestamp crudo:
   * en un feed cronológico el usuario piensa en días, no en instantes, y
   * "2026-06-14T09:31:00Z" lo obliga a traducir cada vez.
   */
  label: string;
  /** Cuántos ítems cayeron en este día. */
  cuenta: number;
  /** Singular/plural de lo que se cuenta — "documento" / "documentos". */
  sustantivo?: [singular: string, plural: string];
  /**
   * Hay ítems posteriores a la última visita del usuario.
   *
   * Sólo tiene sentido cuando las FILAS no traen su propia marca: si cada
   * ítem nuevo ya se señala solo, el badge del encabezado repite lo mismo un
   * renglón más arriba. Por eso la bandeja de documentos no lo usa — sus filas
   * marcan uno por uno.
   */
  nuevo?: boolean;
  className?: string;
}

/**
 * Encabezado de un grupo de un feed cronológico (DS §4bis.7).
 *
 * Ordena una lista con decenas de ítems por jornada: bandeja sincronizada,
 * documentos de terreno, cualquier cosa que llegue de a poco a lo largo del
 * día. Estaba escrito dentro de la bandeja de documentos, que era la única
 * pantalla que agrupaba por día; sacarlo acá es lo que permite que la próxima
 * no lo reinvente con otro tracking y el contador en la tipografía equivocada.
 *
 * El contador va en Geist Mono porque es un DATO — la regla de la fuente en
 * todo el DS. Iba en la sans, que es justo la diferencia que nadie nota de a
 * una y se acumula.
 *
 * OJO dónde se usa: cuando el mismo criterio produce una banda POR ÍTEM (un
 * trabajador que firma cosas sueltas a lo largo de meses) el agrupador sobra y
 * la fecha va en la fila.
 */
export function AgrupadorFecha({
  label,
  cuenta,
  sustantivo = ["documento", "documentos"],
  nuevo = false,
  className,
}: AgrupadorFechaProps) {
  return (
    <div
      className={cn(
        "bg-surface-muted border-border-subtle flex items-center gap-[9px] border-b px-[11px] py-[7px]",
        className,
      )}
    >
      <span className="text-meta text-[11px] font-semibold tracking-[0.04em] uppercase">
        {label}
      </span>
      <span className="fc-num text-soft text-[11px]">
        {cuenta} {cuenta === 1 ? sustantivo[0] : sustantivo[1]}
      </span>
      {nuevo && (
        <span className="bg-primary-100 text-primary-hover inline-flex h-[18px] flex-none items-center rounded-xs px-1.5 text-[10px] font-semibold tracking-[0.03em]">
          NUEVO
        </span>
      )}
    </div>
  );
}
